import { useEffect, useState, useCallback } from "react";
import {
    ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import api from "../../lib/api";
import { ClockCounterClockwise, Broadcast } from "@phosphor-icons/react";

const STATUS_META = {
    success: { label: "Successo", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
    partial: { label: "Parziale", cls: "bg-amber-50 text-amber-700 border-amber-300" },
    error: { label: "Errore", cls: "bg-rose-50 text-rose-700 border-rose-300" },
    idle: { label: "Nessun lavoro", cls: "bg-gray-100 text-gray-600 border-gray-300" },
};

function StatBox({ label, value, testid, accent }) {
    return (
        <div className="border border-gray-200 bg-white p-3" data-testid={testid}>
            <div className={`font-display-black text-2xl tabular-nums ${accent || ""}`}>{value}</div>
            <div className="overline mt-1">{label}</div>
        </div>
    );
}

function MiniStat({ label, value, testid, accent }) {
    return (
        <div className="border border-gray-200 bg-white p-3" data-testid={testid}>
            <div className={`font-semibold text-sm tabular-nums ${accent || ""}`}>{value}</div>
            <div className="overline mt-1">{label}</div>
        </div>
    );
}

const SCHED_STATUS_META = {
    success: { label: "Successo", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
    partial: { label: "Parziale", cls: "bg-amber-50 text-amber-700 border-amber-300" },
    error: { label: "Errore", cls: "bg-rose-50 text-rose-700 border-rose-300" },
    skipped: { label: "Saltato", cls: "bg-gray-100 text-gray-600 border-gray-300" },
};

function fmtDur(s) {
    if (s === null || s === undefined) return "—";
    if (s < 60) return `${Math.round(s)}s`;
    return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

export function PerizieMonitor() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const res = await api.get("/admin/enrich/documents/monitor");
            setData(res.data);
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return <div className="border border-gray-200 bg-white p-5 mb-8 text-sm text-gray-500" data-testid="perizie-monitor-loading">Caricamento monitoraggio scheduler…</div>;
    }
    if (!data) return null;

    const lr = data.last_run;
    const sm = lr ? (STATUS_META[lr.status] || STATUS_META.idle) : null;
    const sh = data.scheduler_health;
    const shStatus = sh ? (sh.running
        ? { label: "In esecuzione", cls: "bg-blue-50 text-blue-700 border-blue-300" }
        : (SCHED_STATUS_META[sh.last_status] || { label: sh.last_status ? sh.last_status : "In attesa", cls: "bg-gray-100 text-gray-600 border-gray-300" }))
        : null;
    const t7 = data.totals_7d, t30 = data.totals_30d;
    const sched = data.scheduler || {};
    const daily30 = data.daily_30d || [];
    const daily7 = data.daily_7d || [];
    const success30 = t30.perizia_found ? Math.round((t30.text_extracted / t30.perizia_found) * 100) : 0;

    const fmtDate = (iso) => iso ? iso.slice(0, 16).replace("T", " ") : "—";

    return (
        <div className="mb-8" data-testid="perizie-monitor">
            <div className="flex items-center gap-2 mb-3">
                <Broadcast size={16} weight="bold" />
                <h2 className="font-display-black text-lg">Monitoraggio scheduler "Trova perizie"</h2>
            </div>

            {/* Scheduler config + last run */}
            <div className="border border-gray-200 bg-white p-5 mb-4" data-testid="perizie-last-run">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                        <ClockCounterClockwise size={22} weight="bold" className="text-gray-400" />
                        <div>
                            <div className="overline">Ultimo run</div>
                            <div className="text-sm font-semibold">
                                {lr ? fmtDate(lr.run_at) : "Nessun run ancora"}
                                {lr && <span className="text-gray-400 font-normal"> · {lr.trigger === "scheduler" ? "notturno" : "manuale"} · {lr.duration_s}s</span>}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {sm && <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${sm.cls}`} data-testid="perizie-last-status">{sm.label}</span>}
                        <span className="text-xs text-gray-400">Programmato ~{String(sched.hour_utc).padStart(2, "0")}:00 UTC · lotti da {sched.batch_limit}</span>
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <StatBox label="Aste analizzate" value={lr?.processed ?? 0} testid="lr-processed" />
                    <StatBox label="PDF/perizie trovate" value={lr?.perizia_found ?? 0} testid="lr-perizia-found" />
                    <StatBox label="Perizie estratte" value={lr?.text_extracted ?? 0} testid="lr-text" />
                    <StatBox label="Deep ready" value={lr?.deep_ready ?? 0} testid="lr-deep-ready" accent="text-indigo-600" />
                    <StatBox label="Fallite" value={lr?.failed ?? 0} testid="lr-failed" />
                    <StatBox label="429 ricevuti" value={lr?.http_429 ?? 0} testid="lr-429" accent={lr?.http_429 ? "text-rose-600" : ""} />
                </div>
                {/* Source used in last run */}
                <div className="mt-3 text-xs text-gray-500" data-testid="perizie-last-source">
                    Fonte documenti utilizzata:{" "}
                    {lr && lr.by_source && Object.keys(lr.by_source).length > 0
                        ? Object.entries(lr.by_source).map(([s, c]) => `${s} (${c})`).join(", ")
                        : "nessuna (nessun PDF scaricato in questo run)"}
                </div>
            </div>

            {/* Resilient scheduler health (FASE 3) */}
            {sh && (
                <div className="border border-gray-200 bg-white p-5 mb-4" data-testid="scheduler-health">
                    <div className="flex items-center justify-between mb-3">
                        <div className="overline">Salute scheduler resiliente · catch-up & pacing</div>
                        {shStatus && (
                            <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${shStatus.cls}`} data-testid="scheduler-health-status">
                                {shStatus.label}
                            </span>
                        )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <MiniStat label="Ultimo run previsto" value={fmtDate(sh.planned_at)} testid="sh-planned" />
                        <MiniStat label="Ultimo run eseguito" value={fmtDate(sh.last_run_at)} testid="sh-last-run" />
                        <MiniStat label="Ultimo successo" value={fmtDate(sh.last_success_at)} testid="sh-last-success" />
                        <MiniStat label="Job recuperati" value={sh.recovered_total ?? 0} testid="sh-recovered" accent="text-indigo-600" />
                        <MiniStat label="Tempo medio" value={fmtDur(sh.avg_duration_s)} testid="sh-avg-duration" />
                        <MiniStat label="Prossimo run" value={fmtDate(sh.next_due_at)} testid="sh-next-due" />
                    </div>
                    {sh.skipped_total ? (
                        <div className="mt-3 text-xs text-gray-500" data-testid="sh-skipped">
                            Job saltati (un altro lavoro era in corso): <span className="font-semibold">{sh.skipped_total}</span>
                        </div>
                    ) : null}
                </div>
            )}

            {/* 7d / 30d totals */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {[{ k: "7g", t: t7, id: "7d" }, { k: "30g", t: t30, id: "30d" }].map(({ k, t, id }) => (
                    <div key={id} className="border border-gray-200 bg-white p-4" data-testid={`perizie-totals-${id}`}>
                        <div className="overline mb-3">Trend ultimi {k}</div>
                        <div className="grid grid-cols-3 gap-3 text-center">
                            <div><div className="font-display-black text-xl tabular-nums">{t.runs}</div><div className="overline">Run</div></div>
                            <div><div className="font-display-black text-xl tabular-nums">{t.perizia_found}</div><div className="overline">Perizie</div></div>
                            <div><div className="font-display-black text-xl tabular-nums text-indigo-600">{t.deep_ready}</div><div className="overline">Deep ready</div></div>
                            <div><div className="font-display-black text-xl tabular-nums">{t.text_extracted}</div><div className="overline">Estratte</div></div>
                            <div><div className="font-display-black text-xl tabular-nums text-rose-600">{t.http_429}</div><div className="overline">429</div></div>
                            <div><div className="font-display-black text-xl tabular-nums">{id === "30d" ? success30 : (t.perizia_found ? Math.round(t.text_extracted / t.perizia_found * 100) : 0)}%</div><div className="overline">Successo</div></div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Cumulative charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                <div className="border border-gray-200 bg-white p-4" data-testid="perizie-chart-cumulative">
                    <div className="overline mb-3">Perizie & Deep Ready accumulate</div>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={daily30} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} />
                            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Line type="monotone" dataKey="cum_perizia" name="Perizie" stroke="#0f766e" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="cum_deep_ready" name="Deep ready" stroke="#4f46e5" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
                <div className="border border-gray-200 bg-white p-4" data-testid="perizie-chart-success">
                    <div className="overline mb-3">% successo download documenti</div>
                    <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={daily30} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} />
                            <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} />
                            <Tooltip formatter={(v) => `${v}%`} />
                            <Area type="monotone" dataKey="download_success_pct" name="Successo %" stroke="#059669" fill="#d1fae5" strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Daily activity 7d */}
            <div className="border border-gray-200 bg-white p-4 mb-4" data-testid="perizie-chart-daily">
                <div className="overline mb-3">Attività giornaliera (7g)</div>
                <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={daily7} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="perizia_found" name="Perizie trovate" fill="#0f766e" />
                        <Bar dataKey="deep_ready" name="Deep ready" fill="#4f46e5" />
                        <Bar dataKey="http_429" name="429" fill="#f43f5e" />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* Source effectiveness 30d */}
            <div className="border border-gray-200 bg-white p-4" data-testid="perizie-sources-30d">
                <div className="overline mb-3">Fonti documenti più efficaci (30g)</div>
                {data.by_source_30d.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {data.by_source_30d.map((s) => (
                            <span key={s.source} className="inline-flex items-center gap-2 text-xs border border-gray-200 px-2 py-1">
                                <span className="font-semibold text-gray-800">{s.source}</span>
                                <span className="tabular-nums text-gray-500">{s.count}</span>
                            </span>
                        ))}
                    </div>
                ) : (
                    <div className="text-sm text-gray-400">Nessun documento scaricato negli ultimi 30 giorni.</div>
                )}
            </div>
        </div>
    );
}
